/**
 * Centralized Configuration Service
 *
 * Validates and exports all environment variables.
 * All configuration must use process.env.VUE_APP_* variables.
 */

class ConfigService {
  constructor() {
    this.validateConfig();
  }

  /**
   * Validate that all required environment variables are present
   */
  validateConfig() {
    const required = [
      'VUE_APP_ENV',
      'VUE_APP_APIDOMAIN',
      'VUE_APP_FIREBASE_API_KEY',
      'VUE_APP_FIREBASE_AUTH_DOMAIN',
      'VUE_APP_FIREBASE_PROJECT_ID',
      'VUE_APP_FIREBASE_STORAGE_BUCKET',
      'VUE_APP_FIREBASE_MESSAGING_SENDER_ID',
      'VUE_APP_FIREBASE_APP_ID',
      'VUE_APP_FIREBASE_MEASUREMENT_ID',
      'VUE_APP_GOOGLE_MAPS_API_KEY',
      'VUE_APP_STRIPE_PUBLISHABLE_KEY',
      'VUE_APP_IPINFO_TOKEN'
    ];

    const missing = required.filter(key => !process.env[key]);

    if (missing.length > 0) {
      console.error('Missing required environment variables:', missing);
      console.error('Please check your .env file configuration.');
      throw new Error(`Missing required environment variables: ${missing.join(', ')}`);
    }
  }

  /**
   * Get current environment
   */
  get environment() {
    return process.env.VUE_APP_ENV || 'development';
  }

  /**
   * Check if running in production
   */
  get isProduction() {
    return this.environment === 'production';
  }

  /**
   * Check if running in staging
   */
  get isStaging() {
    return this.environment === 'staging';
  }

  /**
   * Check if running in development
   */
  get isDevelopment() {
    return this.environment === 'development';
  }

  /**
   * Firebase configuration object
   */
  get firebase() {
    return {
      apiKey: process.env.VUE_APP_FIREBASE_API_KEY,
      authDomain: process.env.VUE_APP_FIREBASE_AUTH_DOMAIN,
      projectId: process.env.VUE_APP_FIREBASE_PROJECT_ID,
      storageBucket: process.env.VUE_APP_FIREBASE_STORAGE_BUCKET,
      messagingSenderId: process.env.VUE_APP_FIREBASE_MESSAGING_SENDER_ID,
      appId: process.env.VUE_APP_FIREBASE_APP_ID,
      measurementId: process.env.VUE_APP_FIREBASE_MEASUREMENT_ID,
    };
  }

  /**
   * Google Maps API configuration
   */
  get googleMaps() {
    return {
      apiKey: process.env.VUE_APP_GOOGLE_MAPS_API_KEY,
      libraries: ['places']
    };
  }

  /**
   * Stripe configuration
   */
  get stripe() {
    return {
      publishableKey: process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY,
    };
  }

  /**
   * Backend API configuration
   */
  get api() {
    return {
      baseURL: process.env.VUE_APP_APIDOMAIN,
    };
  }

  /**
   * IPInfo configuration
   */
  get ipInfo() {
    return {
      token: process.env.VUE_APP_IPINFO_TOKEN,
    };
  }
}

// Export singleton instance
const config = new ConfigService();

// Export for backward compatibility
export const firebaseConfig = config.firebase;
export const googleMapsConfig = config.googleMaps;
export const stripeConfig = config.stripe;
export const apiConfig = config.api;
export const ipInfoConfig = config.ipInfo;

// Export the service instance
export default config;
